import React, { useMemo, useState } from "react";
import { CustomDialog } from "../../../components/StyledComponents";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import {
    ControlpanelPrimaryButton,
    ControlpanelSecondaryButton,
    GrayIconButton,
} from "../../../components/StyledButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import CustomInputHorizontal from "./../../../components/CustomInputHorizontal";
import { toast } from "react-toastify";
import { addRecipients } from "../../../axios/Setting/SettingUrlApi";
import { useLoading } from "../../../context/LoadingContext";
import CustomInputHorizontal1 from "../../../components/CustomInputHorizontal1";

const AddNewRecipientsDialog = (props) => {
    const { t } = useTranslation();
    const [show, hide] = useLoading();

    const [formData, setFormData] = useState({
        email: "",
        remark: "",
    });

    const [validationMessages, setValidationMessages] = useState({
        email: "",
        remark: "",
    });

    const emailRegex = useMemo(
        () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        []
    );

    const validateField = (name, value, data = formData) => {
        setValidationMessages((prev) => {
            const next = { ...prev };

            if (name === "email") {
                if (!value.trim()) {
                    next.email = t("common.required");
                } else if (!emailRegex.test(value.trim())) {
                    next.email = t("validation.email-invalid-format");
                } else {
                    next.email = "";
                }
            }

            if (name === "remark") {
                if (!value.trim()) {
                    next.remark = t("common.required");
                } else {
                    next.remark = "";
                }
            }

            return next;
        });
    };


    const validate = (data = formData) => {
        const v = { email: "", remark: "" };

        if (!data.email?.trim()) {
            v.email = t("common.required");
        } else if (!emailRegex.test(data.email.trim())) {
            v.email = t("validation.email-invalid-format");
        }

        if (!data.remark?.trim()) {
            v.remark = t("common.required");
        }

        setValidationMessages(v);
        return !v.email && !v.remark;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const next = { ...formData, [name]: value };
        setFormData(next);
        validateField(name, value, next);
    };


    const canSubmit = useMemo(() => {
        const emailOk = formData.email.trim() && emailRegex.test(formData.email.trim());
        const remarkOk = !!formData.remark.trim();
        return emailOk && remarkOk;
    }, [formData, emailRegex]);

    const handleSubmit = async () => {
        // Chắn cổng: nếu chưa hợp lệ thì cảnh báo
        if (!validate()) {
            toast.warning(t("validation.fill-required-fields"));
            return;
        }

        const payload = {
            email: formData.email.trim(),
            remark: formData.remark.trim(),
            langId: localStorage.getItem("i18nextLngId")
        };

        show();
        try {
            const response = await addRecipients(payload);
            if (
                response.data.message === "Successful" ||
                response.data.message === "Thành công"
            ) {
                setFormData({ email: "", remark: "" });
                setValidationMessages({ email: "", remark: "" });
                props.handleClose();
                toast.success(t("common.add-recipient-successfully"));
                props.onRefresh();
                hide();
            } else if (response.data.responseCode === 1085) {
                props.handleClose();
                toast.warning(response.data.message);
                props.onRefresh();
                hide();
            } else {
                hide();
                toast.error(response.data.message);
            }
        } catch (error) {
            // setLoading(false);
            if (error.response.data.responseCode === 1085) {
                props.handleClose();
                toast.warn(error.response.data.message);
                props.onRefresh();
                hide();
            } else {
                hide();
                toast.error(error.response.data.message);
            }
        }

        props.handleClose?.();
    };

    const handleClose = () => {
        setFormData({ email: "", remark: "" });
        setValidationMessages({ email: "", remark: "" });
        props.handleClose?.();
    }

    return (
        <CustomDialog maxWidth="sm" open={props.open} onClose={props.handleClose} keepMounted>
            <DialogTitle>
                <div className="flex justify-between">
                    <div className="flex items-center gap-4">
                        <p className="text-xl font-semibold ">
                            {t("common.send-mail-to-another-email-address")}
                        </p>
                    </div>
                    <GrayIconButton onClick={props.handleClose} className="self-start">
                        <CloseIcon />
                    </GrayIconButton>
                </div>
            </DialogTitle>

            <DialogContent>
                <div className="grid grid-cols-1 gap-x-10 gap-y-3 relative">
                    <CustomInputHorizontal1
                        required
                        title={t("common.email")}
                        inputType="text"
                        name="email"
                        id="email"
                        placeholder={t("common.email")}
                        handleInputChange={handleInputChange}
                        value={formData.email}
                    />
                    {validationMessages.email && (
                        <p className="text-[14px] text-red-500 mt-1 pl-[17%]">
                            {validationMessages.email}
                        </p>
                    )}

                    <CustomInputHorizontal1
                        required
                        title={t("common.remark")}
                        inputType="text"
                        name="remark"
                        id="remark"
                        placeholder={t("common.remark")}
                        handleInputChange={handleInputChange}
                        value={formData.remark}
                    />
                    {validationMessages.remark && (
                        <p className="text-[14px] text-red-500 mt-1 pl-[17%]">
                            {validationMessages.remark}
                        </p>
                    )}
                </div>
            </DialogContent>

            <DialogActions>
                <div className="flex gap-2 px-4 py-4">
                    <ControlpanelSecondaryButton onClick={handleClose}>
                        <CloseIcon />
                        <p>{t("button.close")}</p>
                    </ControlpanelSecondaryButton>

                    <ControlpanelPrimaryButton
                        onClick={handleSubmit}
                        disabled={!canSubmit} // disable khi chưa hợp lệ
                    >
                        <AddIcon />
                        <p>{t("button.add")}</p>
                    </ControlpanelPrimaryButton>
                </div>
            </DialogActions>
        </CustomDialog>
    );
};

export default AddNewRecipientsDialog;
